import { useCallback, useRef, useState } from 'react';

import { useLatest } from './useLatest';

/**
 * This is a hook version of `key={}` on components.
 *
 * Example:
 * Our listing view pages use `initiallySaved` because the listing may be saved
 * while the user is viewing the page.
 *
 * When navigating from one listing page to another, we need to reset the `initiallySaved` value
 * because the user is now viewing a new listing.
 *
 * Another approach in this example could be using a zustand store, but that significantly increases
 * the complexity of the code
 */
export function useKeyedState<TState>(
  initialState: TState,
  options?: { key: unknown },
) {
  const key = options?.key ?? initialState;
  const stateValidForKeyRef = useRef<unknown>(key);
  const latestKeyRef = useLatest(key);
  const [state, setState] = useState(initialState);

  const stateValid = key === stateValidForKeyRef.current;
  if (!stateValid) stateValidForKeyRef.current = null;

  const customSetter = useCallback(
    (newValue: TState) => {
      stateValidForKeyRef.current = latestKeyRef.current;
      setState(newValue);
    },
    [latestKeyRef],
  );

  return [stateValid ? state : initialState, customSetter] as const;
}
